import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "../common/Layout";
import { addToCart } from "../features/cart/cart";
import { track } from "../features/analytics/tracker";
import { packOptionsFor, getStockStatus, lowStockLabel } from "../assets/utils/productUtils";
import DeliveryCountdown from "../common/DeliveryCountdown";
import { usePageMeta, useJsonLd } from "../common/usePageMeta";
import { galleryFor, absoluteImageFor } from "../assets/utils/productImages";

function getOrPickHeroVariant(productId, galleryUrls) {
  const key = `td:heroVariant:${String(productId)}`;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Also check idx is still in bounds
      if (parsed && parsed.idx < galleryUrls.length && galleryUrls.includes(parsed.url)) {
        return parsed;
      }
    }
  } catch {}
  const idx     = galleryUrls.length ? Math.floor(Math.random() * galleryUrls.length) : 0;
  const url     = galleryUrls[idx] || null;
  const variant = { idx, url, variant_id: url ? url.split("/").pop() : "none" };
  try { sessionStorage.setItem(key, JSON.stringify(variant)); } catch {}
  return variant;
}

/* ═══════════════════════════════════════════════════════════════════════════
   ProductPage
═══════════════════════════════════════════════════════════════════════════ */
export default function ProductPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [loading,      setLoading]      = useState(true);
  const [err,          setErr]          = useState("");
  const [products,     setProducts]     = useState([]);
  const [activeIdx,    setActiveIdx]    = useState(0);
  const [selectedPack, setSelectedPack] = useState(null);
  const [addState,     setAddState]     = useState("idle"); // "idle" | "adding" | "done"
  const [buyRowOffscreen, setBuyRowOffscreen] = useState(false);

  const heroRef    = useRef({ idx: 0, url: null, variant_id: "none" });
  const lastViewId = useRef(null);
  const buyRowRef  = useRef(null);

  /* mobile sticky buy bar - show once the inline buy button scrolls away */
  useEffect(() => {
    const el = buyRowRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      ([entry]) => setBuyRowOffscreen(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loading, err]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res  = await fetch("https://teardrophydro.co.uk/garageFarm/products");
        const data = await res.json();
        if (!alive) return;
        setProducts(Array.isArray(data) ? data : []);
        setErr("");
      } catch {
        setErr("Couldn't load product.");
        track("product_load_error", { productId: id });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const product = useMemo(
    () => products.find(p => String(p.id) === String(id)),
    [products, id]
  );

  usePageMeta({
    title: product
      ? `${product.name} - Fresh from the farm | TearDrop Hydro`
      : undefined,
    description: product
      ? (product.description ||
         `${product.name} grown hydroponically in Hockley, harvested to order and delivered fresh across Essex.`)
      : undefined,
    path: `/product/${id}`,
  });
  useJsonLd(
    "product",
    product
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description:
            product.description ||
            `${product.name} grown hydroponically in Hockley, Essex.`,
          image: absoluteImageFor(product) || undefined,
          brand: { "@type": "Brand", name: "TearDrop Hydroponics" },
          // product.price is the per-100g base, which isn't always a size we
          // sell, so give search engines the real pack price range instead.
          offers: (() => {
            const prices = packOptionsFor(product).map(o => o.price);
            if (!prices.length) return undefined;
            return {
              "@type": "AggregateOffer",
              url: `https://teardrophydro.co.uk/product/${id}`,
              priceCurrency: "GBP",
              lowPrice: Math.min(...prices).toFixed(2),
              highPrice: Math.max(...prices).toFixed(2),
              offerCount: prices.length,
              availability:
                getStockStatus(product) === "AVAILABLE"
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock",
            };
          })(),
        }
      : null
  );

  const inStock = !!(product?.inStock || product?.stock > 0 || product?.stockGrams > 0 || product?.quantity > 0);

const availableGrams = useMemo(() => {
  if (!product) return 0;
  if (typeof product.stock     === "number") return product.stock;
  if (typeof product.stockGrams === "number") return product.stockGrams;
  if (typeof product.quantity  === "number") return product.quantity;
  return 0;
}, [product]);

  const gallery = useMemo(() => (product ? galleryFor(product) : []), [product]);

  const packOptions = useMemo(
    () => (product ? packOptionsFor(product, availableGrams) : []),
    [product, availableGrams]
  );

  /* reset state on product change */
  useEffect(() => {
    setAddState("idle");
    setSelectedPack(packOptions.find(p => p.available) || null);
  }, [id, packOptions]);

  /* hero variant */
  // In the hero variant effect, clamp the index to actual gallery size
  useEffect(() => {
    if (!product || !gallery.length) return;
    const hv = getOrPickHeroVariant(product.id, gallery);
    heroRef.current = hv;
    // Clamp: if cached idx is out of bounds after image deletion, fall back to 0
    setActiveIdx(Math.min(hv.idx || 0, gallery.length - 1));
  }, [product, gallery]);

  /* product view tracking */
  useEffect(() => {
    if (!product) return;
    if (lastViewId.current === String(product.id)) return;
    lastViewId.current = String(product.id);
    const hv = heroRef.current;
    track("product_view", {
      productId: product.id, name: product.name, category: product.category,
      price: product.price, inStock,
      stockGrams: product.stockGrams ?? null, quantity: product.quantity ?? null,
      hero_variant: hv.variant_id, hero_image: hv.url,
    });
  }, [product, inStock]);

  const handleAdd = () => {
    if (!product || !selectedPack || !selectedPack.available || addState !== "idle") return;
    setAddState("adding");

    const hv             = heroRef.current;
    const currentImage   = gallery[activeIdx] || null;
    const currentVariant = currentImage ? currentImage.split("/").pop() : "none";

    track("add_to_cart", {
      productId: product.id, name: product.name, category: product.category,
      price: product.price, qty: 1, inStock,
      grams: selectedPack.grams, packPrice: selectedPack.price,
      hero_variant: hv.variant_id, hero_image: hv.url,
      gallery_variant: currentVariant, gallery_image: currentImage, gallery_idx: activeIdx,
    });

    addToCart({
      id:             `${product.id}-${selectedPack.grams}`,
      baseId:         product.id,
      name:           `${product.name} (${selectedPack.grams}g)`,
      price:          selectedPack.price,
      qty:            1,
      grams:          selectedPack.grams,
      availableGrams: product.stockGrams  ?? null,
      availableUnits: product.quantity    ?? null,
      image:          currentImage || hv.url || gallery[0],
    });

    setTimeout(() => setAddState("done"),  300);
    setTimeout(() => setAddState("idle"), 1900);
  };

  /* button state */
  const noPackAvailable = packOptions.length > 0 && packOptions.every(p => !p.available);
  const noPackSelected  = !selectedPack;
  const btnDisabled     = !inStock || noPackAvailable || noPackSelected || addState !== "idle";

  const btnLabel = addState === "adding"
    ? "Adding…"
    : addState === "done"
      ? "✓ Added to cart"
      : noPackAvailable
        ? "Out of stock"
        : noPackSelected
          ? "Select a size"
          : "Add to cart";

  const btnHint = noPackAvailable
    ? "This product is currently out of stock."
    : noPackSelected
      ? "Choose a pack size above to continue."
      : null;

  if (loading) {
    return (
      <Layout>
        <div className="container section">
          <p className="muted">Loading…</p>
        </div>
      </Layout>
    );
  }

  if (err || !product) {
    return (
      <Layout>
        <div className="container section">
          <p className="muted">{err || "Product not found."}</p>
          <button className="btn btn-ghost" style={{ marginTop: 12 }} onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="product-page container">

        {/* Breadcrumb - existing .crumbs */}
        <nav className="crumbs">
          <Link to="/browse-products?category=all">Browse</Link>
          <span>/</span>
          <span aria-current="page">{product.name}</span>
        </nav>

        <div className="product-grid">

          {/* ── Media ───────────────────────────────────────────── */}
          <div className="product-media">
            <motion.div
              key={gallery[activeIdx] || "noimg"}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.22 }}
              className="product-hero"
              style={{ backgroundImage: `url(${gallery[activeIdx]})` }}
              role="img"
              aria-label={product.name}
              onAnimationComplete={() => {
                const img = gallery[activeIdx];
                if (!img) return;
                track("product_image_view", {
                  productId: product.id, idx: activeIdx,
                  variant: img.split("/").pop(),
                });
              }}
            />

            {gallery.length > 1 && (
              <div className="thumb-row" role="list">
                {gallery.map((src, i) => (
                  <button
                    key={src}
                    role="listitem"
                    className={`thumb ${i === activeIdx ? "is-active" : ""}`}
                    style={{ backgroundImage: `url(${src})` }}
                    onClick={() => {
                      setActiveIdx(i);
                      track("product_gallery_thumb_click", {
                        productId: product.id, idx: i,
                        variant: src.split("/").pop(), total: gallery.length,
                        hero_variant: heroRef.current.variant_id,
                      });
                    }}
                    aria-label={`View image ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Info ────────────────────────────────────────────── */}
          <div className="product-info">
            <h1 className="h2" style={{ marginTop: 0 }}>{product.name}</h1>
            <p className="muted" style={{ marginTop: 6 }}>{product.description}</p>

            {/* Stock indicators - .badge, .badge-out, .pill-available all exist */}
            <div className="product-meta">
              {inStock ? (
                <span className="badge pill-available">
                  <span style={{
                    display: "inline-block", width: 7, height: 7,
                    borderRadius: "50%", background: "currentColor", marginRight: 4,
                  }} />
                  Available
                </span>
              ) : (
                <span className="badge badge-out">Out of stock</span>
              )}

              {inStock && lowStockLabel(product) && (
                <span className="badge pill-available pill-low">{lowStockLabel(product)}</span>
              )}
            </div>

            {/* Pack size selector - existing .pack-options / .pack-btn / .is-active */}
            {packOptions.length > 0 && (
              <div style={{ marginTop: "var(--s-4)" }}>
                <p className="eyebrow" style={{ marginBottom: "var(--s-3)" }}>Choose a size</p>
                <div className="pack-options">
                  {packOptions.map(p => (
                    <button
                      key={p.grams}
                      disabled={!p.available}
                      className={`pack-btn ${selectedPack?.grams === p.grams ? "is-active" : ""}`}
                      onClick={() => {
                        if (!p.available) return;
                        setSelectedPack(p);
                        track("product_pack_select", { productId: product.id, name: `${p.grams}g`, grams: p.grams, price: p.price });
                      }}
                      aria-pressed={selectedPack?.grams === p.grams}
                    >
                      {/* OOS label - positioned absolutely inside pack-btn */}
                      {!p.available && (
                        <span className="pack-oos-label">🔒 Out of stock</span>
                      )}
                      <span style={{ fontSize: 15, fontWeight: 600 }}>{p.grams}g</span>
                      <strong>£{p.price.toFixed(2)}</strong>
                      {/* per-100g line - uses existing .small .muted */}
                      <span className="small muted">£{p.per100g.toFixed(2)} / 100g</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Add to cart */}
            <div ref={buyRowRef} className="buy-row" style={{ marginTop: "var(--s-5)", alignItems: "flex-start" }}>
              <button
                className={`btn ${
                  addState === "done"
                    ? "btn-secondary"
                    : btnDisabled
                      ? "btn-ghost"
                      : "btn-primary"
                }`}
                style={{
                  width: "100%", maxWidth: 280,
                  opacity: btnDisabled && addState === "idle" ? 0.55 : 1,
                  cursor:  btnDisabled ? "not-allowed" : "pointer",
                  pointerEvents: addState === "adding" ? "none" : undefined,
                }}
                onClick={handleAdd}
                disabled={btnDisabled}
                aria-busy={addState === "adding"}
              >
                {btnLabel}
              </button>

              {btnHint && (
                <p className="muted" style={{ fontSize: 13, marginTop: "var(--s-2)" }}>
                  {btnHint}
                </p>
              )}
              {inStock && <DeliveryCountdown placement="product" style={{ marginTop: "var(--s-3)" }} />}
            </div>

            {/* Facts */}
            <ul className="facts">
              {product.category && (
                <li>
                  <span className="eyebrow">Category</span>
                  {product.category}
                </li>
              )}
              {selectedPack && (
                <li>
                  <span className="eyebrow">Selected</span>
                  {selectedPack.grams}g · £{selectedPack.price.toFixed(2)}
                </li>
              )}
              {product.stockGrams > 0 && (
                <li><span className="eyebrow">Stock</span> {product.stockGrams}g</li>
              )}
              {!product.stockGrams && product.quantity > 0 && (
                <li><span className="eyebrow">Stock</span> {product.quantity} units</li>
              )}
            </ul>
          </div>
        </div>

        {/* Mobile sticky buy bar */}
        <div
          className={`buybar ${buyRowOffscreen && inStock && !noPackAvailable ? "is-visible" : ""}`}
          aria-hidden={!buyRowOffscreen}
        >
          <div className="buybar__info">
            <span className="buybar__name">{product.name}</span>
            <span className="buybar__price">
              {selectedPack ? `${selectedPack.grams}g · £${selectedPack.price.toFixed(2)}` : "Select a size"}
            </span>
          </div>
          <button
            className={`btn ${addState === "done" ? "btn-secondary" : "btn-primary"}`}
            onClick={handleAdd}
            disabled={btnDisabled}
            tabIndex={buyRowOffscreen ? 0 : -1}
          >
            {addState === "adding" ? "Adding…" : addState === "done" ? "✓ Added" : "Add to cart"}
          </button>
        </div>

        {/* Toast - existing .add-toast / .show */}
        <div
          className={`add-toast ${addState === "done" ? "show" : ""}`}
          role="status"
          aria-live="polite"
        >
          Added to cart ✓{" "}
          <Link
            to="/cart"
            className="footer-link"
            style={{ marginLeft: 8 }}
            onClick={() => track("go_to_cart_from_toast", { productId: product.id })}
          >
            View cart
          </Link>
        </div>

      </div>
    </Layout>
  );
}